import { z } from "zod";
import { IdSchema, IsoDateTimeSchema } from "./primitives.js";
/**
 * Visits imported from a previous EMR, exactly as that clinic recorded them.
 *
 * These are not encounters and never become encounters: an encounter starts
 * now and a note is signed here by a live clinician, so replaying old visits
 * through those tables would put another clinic's work under this clinic's
 * signature. `noteSignedInSource` says the previous EMR marked the note
 * signed; it asserts nothing about this system. Every row is read-only.
 *
 * Mirrors api.list_patient_legacy_visits.
 */
export const LegacyVisitKindSchema = z.enum(["appointment", "note"]);
export const LegacyVisitStatusSchema = z.enum([
    "completed",
    "cancelled",
    "no_show",
    "booked",
    "unknown",
]);
export const LegacyVisitProviderKindSchema = z.enum([
    "physician",
    "staff",
    /** A placeholder the previous EMR used for a self-booked appointment. */
    "online_booking",
    "unknown",
]);
export const LegacyVisitMatchKindSchema = z.enum(["exact", "date_only", "none"]);
export const LegacyVisitNoteSectionsSchema = z
    .object({
    history: z.string().optional(),
    physicalExam: z.string().optional(),
    assessmentPlan: z.string().optional(),
    medicationsPrescribed: z.string().optional(),
    vitals: z.string().optional(),
})
    .strict();
export const LegacyVisitNoteSchema = z
    .object({
    text: z.string().max(100_000),
    sections: LegacyVisitNoteSectionsSchema.optional(),
    /** The people named here worked at the previous clinic, not this one. */
    authorDisplay: z.string().max(160).optional(),
    authorRole: z.string().max(40).optional(),
    authoredAt: IsoDateTimeSchema.optional(),
    reviewerDisplay: z.string().max(160).optional(),
    reviewerBillingId: z.string().max(40).optional(),
    reviewedAt: IsoDateTimeSchema.optional(),
    sourceReference: z.string().max(240).optional(),
})
    .passthrough();
export const LegacyVisitProviderSchema = z
    .object({
    display: z.string().max(160).optional(),
    billingId: z.string().max(40).optional(),
    kind: LegacyVisitProviderKindSchema,
})
    .passthrough();
export const LegacyVisitSchema = z
    .object({
    visitId: IdSchema,
    kind: LegacyVisitKindSchema,
    linkedVisitId: IdSchema.optional(),
    occurredAt: IsoDateTimeSchema,
    occurredOn: z.string().date(),
    timeKnown: z.boolean().default(true),
    durationMinutes: z.number().int().nonnegative().optional(),
    status: LegacyVisitStatusSchema,
    /** The status word the previous EMR used, kept verbatim. */
    sourceStatus: z.string().max(40).optional(),
    noteSignedInSource: z.boolean().default(false),
    provider: LegacyVisitProviderSchema.optional(),
    reason: z.string().max(500).optional(),
    staffNotes: z.string().max(4000).optional(),
    noteType: z.string().max(80).optional(),
    matchKind: LegacyVisitMatchKindSchema.optional(),
    hasNote: z.boolean().default(false),
    /** Absent for roles that may see the visit but not its clinical content. */
    note: LegacyVisitNoteSchema.optional(),
    source: z
        .object({ system: z.string().min(1).max(160), reference: z.string().min(1).max(240) })
        .passthrough(),
    version: z.number().int().positive(),
})
    .passthrough();
export const PatientLegacyVisitsSchema = z
    .object({
    patientId: IdSchema,
    siteId: IdSchema,
    items: z.array(LegacyVisitSchema),
    total: z.number().int().nonnegative(),
})
    .passthrough();
//# sourceMappingURL=patient-legacy-visits.js.map